import { Suspense, useState } from 'react';

import { useGetContext } from '@api/lendingAPI/lapiHooks';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { Modal } from '@iwoca/orion';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';

import styles from './AccountCard.module.css';
import { ErrorBoundary } from '../../../errorBoundary/ErrorBoundary';
import { FormattedCurrency } from '../../../iwIntlProvider/FormattedCurrency';
import { ArrowSVG } from '../../../svgComponents/arrowSVG/index';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import ProductType from '../../ui/productType/ProductType';
import AccountHistoryTable from '../accountHistoryTable/AccountHistoryTable';

type AccountCardProps = {
    accountId: string;
    productType: string;
    balance: number;
    duration: {
        amount: number;
        unit: 'days' | 'months' | 'years';
    };
};

const AccountCard = ({
    accountId,
    productType,
    balance,
    duration
}: AccountCardProps) => {
    const [areTransactionsOpen, setAreTransactionsOpen] = useState(false);

    const handleModal = () => setAreTransactionsOpen(!areTransactionsOpen);

    const durationInMonths = getDurationInMonths(duration);

    const { data } = useGetContext();

    const isAccessUser = data?.logged_in_user_type === 'access_user';

    return (
        <>
            <button
                className={styles.accountCardWithHistory}
                onClick={handleModal}
            >
                <span className={styles.accountInfo}>
                    <span className={styles.accountType}>
                        <ProductType
                            productType={productType}
                            durationInMonths={durationInMonths}
                        />
                    </span>

                    <span className={styles.balance}>
                        <span className={styles.balanceLabel}>
                            <FormattedMessage
                                id="_.account_history.balanceLabel"
                                defaultMessage="outstanding balance"
                            />
                        </span>

                        <span
                            data-testid="outstanding-balance"
                            className={styles.balanceAmount}
                        >
                            <FormattedCurrency value={balance} />
                        </span>
                    </span>
                </span>

                <span className={styles.activeDivider} />

                <span className={styles.accountCTA}>
                    {isAccessUser ? (
                        <FormattedMessage
                            id="_.account_history.accessUserAccountCTA"
                            defaultMessage="See history"
                        />
                    ) : (
                        <FormattedMessage
                            id="_.account_history.accountCTA"
                            defaultMessage="See your transaction history"
                        />
                    )}
                    <ArrowSVG />
                </span>
            </button>

            <Modal
                style={{ maxWidth: '1000px' }}
                isOpen={areTransactionsOpen}
                onClose={handleModal}
                closeIcon
            >
                <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                    <Suspense
                        fallback={
                            <div
                                className="space-y-l"
                                data-testid="account-history-table-loading"
                            >
                                <Skeleton />
                                <Skeleton />
                                <Skeleton />
                            </div>
                        }
                    >
                        <AccountHistoryTable
                            accountId={accountId}
                            productType={productType}
                            durationInMonths={durationInMonths}
                        />
                    </Suspense>
                </ErrorBoundary>
            </Modal>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default AccountCard;
